import { clamp } from '@lemonade-hq/ts-helpers';
import { useState, useCallback, useEffect } from 'react';
import { useDebouncedSetState } from '../../../common/hooks/useDebouncedSetState/useDebouncedSetState.esm.js';
import { isMobile } from '../../helpers/browsers.esm.js';
import { useUserInteractionType } from '../useUserInteractionType/useUserInteractionType.esm.js';

function _array_like_to_array(arr, len) {
    if (len == null || len > arr.length) len = arr.length;
    for(var i = 0, arr2 = new Array(len); i < len; i++)arr2[i] = arr[i];
    return arr2;
}
function _array_with_holes(arr) {
    if (Array.isArray(arr)) return arr;
}
function _iterable_to_array_limit(arr, i) {
    var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];
    if (_i == null) return;
    var _arr = [];
    var _n = true;
    var _d = false;
    var _s, _e;
    try {
        for(_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true){
            _arr.push(_s.value);
            if (i && _arr.length === i) break;
        }
    } catch (err) {
        _d = true;
        _e = err;
    } finally{
        try {
            if (!_n && _i["return"] != null) _i["return"]();
        } finally{
            if (_d) throw _e;
        }
    }
    return _arr;
}
function _non_iterable_rest() {
    throw new TypeError("Invalid attempt to destructure non-iterable instance.\\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _sliced_to_array(arr, i) {
    return _array_with_holes(arr) || _iterable_to_array_limit(arr, i) || _unsupported_iterable_to_array(arr, i) || _non_iterable_rest();
}
function _unsupported_iterable_to_array(o, minLen) {
    if (!o) return;
    if (typeof o === "string") return _array_like_to_array(o, minLen);
    var n = Object.prototype.toString.call(o).slice(8, -1);
    if (n === "Object" && o.constructor) n = o.constructor.name;
    if (n === "Map" || n === "Set") return Array.from(n);
    if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _array_like_to_array(o, minLen);
}
var CLEAR_SEARCH_DEBOUNCE_DELAY = 1000;
var MENU_SELECTOR = 'ul[role="listbox"], ul[role="menu"], ul[role="menubar"]';
var TABBABLE_SELECTOR = '[role="menuitem"], [role="menuitemcheckbox"], [role="menuitemradio"], a, button, [tabindex="0"]';
var closeKeys = [
    "Escape",
    "Tab"
];
var openKeys = [
    "ArrowUp",
    "ArrowDown",
    "Enter",
    " "
];
var searchFocusKeysRegex = new RegExp(/^(.|Backspace)$/g);
var useDropdownA11y = function(param) {
    var node = param.node, _param_autoOpen = param.autoOpen, autoOpen = _param_autoOpen === void 0 ? false : _param_autoOpen, _param_supportKeyboard = param.supportKeyboard, supportKeyboard = _param_supportKeyboard === void 0 ? false : _param_supportKeyboard, _param_searchable = param.searchable, searchable = _param_searchable === void 0 ? false : _param_searchable, _param_disabled = param.disabled, disabled = _param_disabled === void 0 ? false : _param_disabled, _param_enableKeyFocus = param.enableKeyFocus, enableKeyFocus = _param_enableKeyFocus === void 0 ? true : _param_enableKeyFocus, _param_closeOnTab = param.closeOnTab, closeOnTab = _param_closeOnTab === void 0 ? true : _param_closeOnTab;
    var _useState = _sliced_to_array(useState(autoOpen), 2), isOpen = _useState[0], setIsOpen = _useState[1];
    var _useState1 = _sliced_to_array(useState(0), 2), focused = _useState1[0], setFocused = _useState1[1];
    var _useState2 = _sliced_to_array(useState(""), 2), search = _useState2[0], setSearch = _useState2[1];
    var userInteractionType = useUserInteractionType();
    var getListContainer = useCallback(function() {
        var _node_current;
        return (_node_current = node.current) === null || _node_current === void 0 ? void 0 : _node_current.querySelector(MENU_SELECTOR);
    }, [
        node
    ]);
    var close = useCallback(function() {
        var _node_current;
        setIsOpen(false);
        (_node_current = node.current) === null || _node_current === void 0 ? void 0 : _node_current.focus();
    }, [
        node
    ]);
    var debouncedSetSearch = useDebouncedSetState(CLEAR_SEARCH_DEBOUNCE_DELAY, setSearch);
    var getElement = useCallback(function(focusedIndex) {
        var _getListContainer;
        var li = (_getListContainer = getListContainer()) === null || _getListContainer === void 0 ? void 0 : _getListContainer.childNodes[focusedIndex];
        var item = li === null || li === void 0 ? void 0 : li.querySelector(TABBABLE_SELECTOR);
        return item != null ? item : li;
    }, [
        getListContainer
    ]);
    var handleClick = function(e) {
        var _node_current;
        if (disabled || ((_node_current = node.current) === null || _node_current === void 0 ? void 0 : _node_current.contains(e.target))) return;
        setIsOpen(false);
    };
    var focusBySearchTerm = useCallback(function(term) {
        var _getListContainer;
        var _getListContainer_children;
        var children = Array.from((_getListContainer_children = (_getListContainer = getListContainer()) === null || _getListContainer === void 0 ? void 0 : _getListContainer.children) !== null && _getListContainer_children !== void 0 ? _getListContainer_children : []);
        var elemIndex = children.findIndex(function(child) {
            var _child_getAttribute;
            return (_child_getAttribute = child.getAttribute("value")) === null || _child_getAttribute === void 0 ? void 0 : _child_getAttribute.toLocaleLowerCase().startsWith(term);
        });
        var hasElement = elemIndex !== -1;
        setSearch(hasElement ? term : "");
        setFocused(hasElement ? elemIndex : 0);
        debouncedSetSearch("");
    }, [
        debouncedSetSearch,
        getListContainer
    ]);
    var handlePress = useCallback(function(e) {
        var _getListContainer;
        if (disabled) return;
        if (!node.current) return;
        var _getListContainer_children;
        var children = Array.from((_getListContainer_children = (_getListContainer = getListContainer()) === null || _getListContainer === void 0 ? void 0 : _getListContainer.children) !== null && _getListContainer_children !== void 0 ? _getListContainer_children : []);
        if (isOpen && closeKeys.includes(e.key) && (closeOnTab || e.key !== "Tab")) {
            close();
            return;
        } else if (!isOpen && node.current.contains(document.activeElement) && openKeys.includes(e.key)) {
            e.preventDefault();
            setIsOpen(true);
        } else if (isOpen && e.key === "Enter") {
            e.preventDefault();
            e.stopPropagation();
            var element = getElement(focused);
            if (element && !element.hasAttribute("disabled")) {
                element.click();
            }
        } else if (isOpen && searchable && searchFocusKeysRegex.test(e.key)) {
            var _node_current_querySelector;
            (_node_current_querySelector = node.current.querySelector("input")) === null || _node_current_querySelector === void 0 ? void 0 : _node_current_querySelector.focus();
        } else if (isOpen && (e.key === "ArrowUp" || e.key === "ArrowLeft")) {
            var _getElement;
            e.preventDefault();
            var focusElement = clamp(0, children.length - 1, focused - 1);
            setFocused(focusElement);
            (_getElement = getElement(focusElement)) === null || _getElement === void 0 ? void 0 : _getElement.focus();
            return;
        } else if (isOpen && (e.key === "ArrowDown" || e.key === "ArrowRight")) {
            var _getElement1;
            e.preventDefault();
            var focusElement1 = clamp(0, children.length - 1, focused + 1);
            setFocused(focusElement1);
            (_getElement1 = getElement(focusElement1)) === null || _getElement1 === void 0 ? void 0 : _getElement1.focus();
            return;
        } else if (isOpen && e.key === "Home") {
            var _getElement2;
            e.preventDefault();
            var focusElement2 = 0;
            setFocused(focusElement2);
            (_getElement2 = getElement(focusElement2)) === null || _getElement2 === void 0 ? void 0 : _getElement2.focus();
            return;
        } else if (isOpen && e.key === "End") {
            var _getElement3;
            e.preventDefault();
            var focusElement3 = children.length - 1;
            setFocused(focusElement3);
            (_getElement3 = getElement(focusElement3)) === null || _getElement3 === void 0 ? void 0 : _getElement3.focus();
            return;
        }
        if (enableKeyFocus && /[a-z|A-Z|0-9]/.test(e.key)) {
            focusBySearchTerm(search + e.key);
        }
    }, [
        disabled,
        enableKeyFocus,
        focusBySearchTerm,
        focused,
        getElement,
        isOpen,
        node,
        search,
        searchable,
        getListContainer,
        closeOnTab,
        close
    ]);
    useEffect(function() {
        if (disabled && isOpen) {
            close();
        }
    }, [
        disabled,
        close,
        isOpen
    ]);
    useEffect(function() {
        var _getListContainer;
        if (!node.current || isMobile()) return;
        node.current.style.pointerEvents = userInteractionType === "keyboard" ? "none" : "auto";
        (_getListContainer = getListContainer()) === null || _getListContainer === void 0 ? void 0 : _getListContainer.childNodes.forEach(function(c) {
            return c.blur();
        });
    }, [
        isOpen,
        userInteractionType,
        getListContainer,
        node
    ]);
    useEffect(function() {
        var _getElement;
        if (isOpen && !searchable) (_getElement = getElement(focused)) === null || _getElement === void 0 ? void 0 : _getElement.focus();
    }, [
        focused,
        getElement,
        isOpen,
        searchable
    ]);
    useEffect(function() {
        window.addEventListener("mousedown", handleClick);
        window.addEventListener("touchstart", handleClick);
        if (supportKeyboard) {
            window.addEventListener("keydown", handlePress);
        }
        return function() {
            window.removeEventListener("mousedown", handleClick);
            window.removeEventListener("touchstart", handleClick);
            if (supportKeyboard) {
                window.removeEventListener("keydown", handlePress);
            }
        };
    }, [
        handlePress
    ]); // eslint-disable-line react-hooks/exhaustive-deps
    var guardedSetIsOpen = useCallback(function(value) {
        if (disabled) return;
        setIsOpen(value);
    }, [
        disabled
    ]);
    return {
        isOpen: isOpen,
        setIsOpen: guardedSetIsOpen
    };
};

export { openKeys, useDropdownA11y };
